import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Pencil, Trash2, FileSearch, Hash, Award, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import RenameClinicCodeModal from "./RenameClinicCodeModal";
import { SealModal } from "./ProfessionalsTable";
import { CLINIC_TYPES } from "../../lib/clinicMeta";

const TIER_CHIP = {
  ouro:   "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]",
  prata:  "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900",
  bronze: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white",
};

// Iter 68.66.h — tipos extensos incluindo os do universo FP + o default RAF.
// O admin pode escolher qualquer um; se ficar em branco a vitrine usa
// "Clínica de Reabilitação" (default sensato aplicado no backend).
const ESTABLISHMENT_OPTIONS = [
  ...CLINIC_TYPES.map((t) => t.value).filter((v) => v !== "Outros"),
  "Clínica de Reabilitação",
  "Hospital Psiquiátrico",
  "Consultório Psiquiátrico",
  "ILPI (Instituição de Longa Permanência)",
  "Casa de Repouso",
  "Centro-Dia do Idoso",
  "Home Care Geriátrico",
  "Clínica de Fisioterapia Geriátrica",
  "Outros",
];

export const ClinicsTable = ({ clinics, onEdit, onRemove, onRefresh }) => {
  const [renaming, setRenaming] = useState(null);
  const [sealing, setSealing] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [savingType, setSavingType] = useState(null);
  const sorted = [...clinics].sort((a, b) => {
    const na = parseInt(((a.code || "").match(/\d+/) || [9999])[0], 10);
    const nb = parseInt(((b.code || "").match(/\d+/) || [9999])[0], 10);
    return na - nb;
  });

  // Iter 68.66.h — troca inline do tipo de estabelecimento, sem abrir o
  // formulário completo. Backend já aceita `establishment_type` no
  // `PATCH /admin/clinics/{id}` (ClinicProfileInput.establishment_type).
  const patchEstablishmentType = async (clinic, value) => {
    setSavingType(clinic.id);
    try {
      await api.patch(`/admin/clinics/${clinic.id}`, { establishment_type: value });
      toast.success(`Tipo atualizado para "${value}"`);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar tipo");
    } finally {
      setSavingType(null);
    }
  };

  // Repair one-shot: força `is_public=true` em clínicas certificadas cujo
  // flag ficou `false` por legado. Corrige unidades certificadas que não
  // apareciam na vitrine pública /unidades. Idempotente e seguro.
  const runRepairVisibility = async () => {
    if (repairing) return;
    if (!window.confirm("Reparar visibilidade pública das unidades certificadas?\n\nIsso força `is_public=true` em toda clínica com selo Ouro/Prata/Bronze ativo. Ação segura e idempotente.")) return;
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/clinics/repair-public-visibility");
      if (data.fixed_count === 0) {
        toast.success("Tudo em ordem — nenhuma unidade precisou de reparo.");
      } else {
        const names = (data.fixed || []).map(f => `${f.code} · ${f.name}`).join("\n");
        toast.success(`${data.fixed_count} unidade(s) reparada(s) — agora aparecem em /unidades.`);
        window.alert(`Reparadas ${data.fixed_count} unidade(s):\n\n${names}\n\nAtualize a página /unidades para ver.`);
      }
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao reparar");
    } finally {
      setRepairing(false);
    }
  };

  const isActive = (c) => {
    if (!c.certification_tier) return false;
    if (!["paid", "waived"].includes((c.certification_payment_status || "").toLowerCase())) return false;
    if (!c.certification_expires_at) return false;
    return new Date(c.certification_expires_at) > new Date();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="admin-clinics-panel">
      <div className="p-4 border-b border-slate-200 flex items-center gap-3">
        <Building2 className="w-5 h-5 text-slate-500" />
        <h2 className="font-display font-bold text-lg text-[#0B2B62]">Unidades Parceiras ({clinics.length})</h2>
        <button
          type="button"
          onClick={runRepairVisibility}
          disabled={repairing}
          data-testid="clinics-repair-visibility-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#0B2B62] hover:text-[#0B2B62] disabled:opacity-60 disabled:cursor-wait transition-colors"
          title="Corrige unidades certificadas que não aparecem em /unidades por legado (is_public=false)"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          {repairing ? "Reparando..." : "Reparar visibilidade pública"}
        </button>
      </div>
      <div className="lg:hidden px-4 pt-2 text-[11px] text-slate-400 italic">← Arraste para o lado para ver todas as colunas →</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left">Código</th>
              <th className="px-4 py-2.5 text-left">Nome</th>
              <th className="px-4 py-2.5 text-left">Tipo (vitrine)</th>
              <th className="px-4 py-2.5 text-left">Cidade</th>
              <th className="px-4 py-2.5 text-left">E-mail</th>
              <th className="px-4 py-2.5 text-left">Vagas</th>
              <th className="px-4 py-2.5 text-left">Selo</th>
              <th className="px-4 py-2.5 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((c) => (
              <tr key={c.id} data-testid={`admin-clinic-${c.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest bg-[#0B2B62] text-white px-1.5 py-0.5 rounded" data-testid={`admin-clinic-code-${c.id}`}>
                    {c.code || "—"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-slate-800 font-medium">{c.name}</td>
                <td className="px-4 py-2.5">
                  <select
                    value={c.establishment_type || ""}
                    onChange={(e) => patchEstablishmentType(c, e.target.value)}
                    disabled={savingType === c.id}
                    data-testid={`admin-clinic-est-type-${c.id}`}
                    title="Tipo exibido na vitrine pública. Vazio = 'Clínica de Reabilitação' (default)."
                    className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white max-w-[180px] disabled:opacity-50">
                    <option value="">— (default: Reabilitação)</option>
                    {ESTABLISHMENT_OPTIONS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2.5 text-slate-600 text-xs">{c.city}/{c.state}</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs">{c.email || "—"}</td>
                <td className="px-4 py-2.5">{c.beds_available ?? 0}</td>
                <td className="px-4 py-2.5">
                  {c.certification_tier ? (
                    <div className="flex flex-col gap-0.5">
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest ${TIER_CHIP[c.certification_tier] || ""} ${isActive(c) ? "" : "opacity-50"}`}>
                        <Award className="w-2.5 h-2.5 inline mr-0.5" />
                        {c.certification_tier}
                      </span>
                      {c.certification_is_lifetime ? (
                        <span className="text-[9px] px-1 py-0.5 rounded uppercase font-bold bg-amber-100 text-amber-900 border border-amber-300"
                          title="Selo vitalício — não expira">
                          ★ vitalício
                        </span>
                      ) : (
                        <span className="text-[9px] px-1 py-0.5 rounded uppercase font-bold text-slate-500">
                          {c.certification_payment_status || "?"}
                        </span>
                      )}
                    </div>
                  ) : <span className="text-[10px] text-slate-400">sem selo</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => setSealing(c)} data-testid={`admin-clinic-seal-${c.id}`}
                      title="Conceder/revogar selo Ouro/Prata/Bronze"
                      className="text-amber-600 hover:bg-amber-500 hover:text-white p-1.5 rounded-md border border-amber-500/30 transition-colors">
                      <Award className="w-4 h-4" />
                    </button>
                    <button onClick={() => setRenaming(c)} data-testid={`admin-clinic-rename-code-${c.id}`}
                      title="Renomear código canônico (P001, P002…)"
                      className="text-slate-500 hover:text-[#0B2B62] p-1.5 rounded-md hover:bg-slate-100">
                      <Hash className="w-4 h-4" />
                    </button>
                    <Link to={`/admin/clinicas/${c.id}`} data-testid={`admin-clinic-audit-${c.id}`}
                      title="Abrir cadastro completo em modo admin"
                      className="text-[#0B2B62] hover:text-white hover:bg-[#0B2B62] p-1.5 rounded-md transition-colors border border-[#0B2B62]/20">
                      <FileSearch className="w-4 h-4" />
                    </Link>
                    <button onClick={() => onEdit({ ...c })} data-testid={`admin-clinic-edit-${c.id}`} title="Edição rápida" className="text-slate-500 hover:text-[#0B2B62] p-1.5 rounded-md hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => onRemove(c.id, c.name)} data-testid={`admin-clinic-del-${c.id}`} className="text-red-500 hover:text-red-700 p-1.5 rounded-md hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renaming && (
        <RenameClinicCodeModal clinic={renaming} allClinics={clinics}
          onClose={() => setRenaming(null)} onSaved={() => onRefresh && onRefresh()} />
      )}
      {sealing && (
        <SealModal
          target={sealing}
          endpoint={`/admin/clinics/${sealing.id}/certification`}
          kind="clinica"
          onClose={() => setSealing(null)}
          onSaved={() => { setSealing(null); onRefresh && onRefresh(); }}
        />
      )}
    </div>
  );
};

export default ClinicsTable;
